import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Search,
  Sparkles,
  Layers,
  Grid3X3,
} from 'lucide-react';
import * as mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateJSON } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { useToast } from '../../Utils/toastContext';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const BASE_URL = process.env.REACT_APP_BASE_URL;
export default function ContractList() {
  const [contracts, setContracts] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [isLoading, setIsLoading] = useState('');

  const [mode, setMode] = useState(null);
  const location = useLocation();
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 10;
  const navigate = useNavigate();
  const [editorData, setEditorData] = useState(null);
  const [showRecords, setShowRecords] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getContractList = async () => {
    try {
      setIsLoading(true);
      const response = await GetAPIcalls(
        `/api/v1/customer/contracts?page=${currentPage}&page_size=${PAGE_SIZE}&search=${filterText}`,
      );

      if (response.status == 200) {
        setContracts(response.data.contracts || []);
        setTotalPages(response.data.pagination.total_pages || 1);
        setTotalCount(response.data.pagination.total || 0);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };
  useEffect(() => {
    getContractList();
  }, [currentPage, filterText]);
  return (
    <div className="h-full g:min-w-[78vw] lg:max-w-[80vw] rounded-lg shadow-lg bg-gradient-to-br from-[#f5f7ff] via-white to-[#f8faff] text-gray-800 flex flex-col">
      {/* TOP HEADER */}
      <div className="px-8 py-6 border-b bg-white/60 rounded-lg shadow-lg backdrop-blur-xl flex items-center justify-between">
        {/* LEFT BRAND */}
        <div className="flex items-center gap-3 ">
          <div className="w-10 h-10 rounded-xl bg-[#375AE7] flex items-center justify-center text-white shadow">
            <Layers size={18} />
          </div>

          <div>
            <h1 className="text-xl font-bold">Contract Studio</h1>
            <p className="text-xs text-gray-500">Manage your legal contracts</p>
          </div>
        </div>

        {/* RIGHT ACTIONS */}
        <div className="flex items-center gap-3">
          {/* SEARCH */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-4 py-2 w-72">
            <Search size={16} className="text-gray-500" />
            <input
              placeholder="Search contract..."
              className="bg-transparent outline-none text-sm w-full"
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>
      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-auto px-8 py-6">
        {/* TITLE ROW */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold">Your Contracts</h2>
            <p className="text-sm text-gray-500">Select a contract to view</p>
          </div>
        </div>

        {/* TEMPLATE GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {isLoading
            ? Array.from({ length: 8 }).map((_, index) => (
                <ContractCardShimmer key={index} />
              ))
            : contracts.map((t) => (
                <div
                  key={t.id}
                  onClick={() => {
                    navigate(`/view-template/${t.id}`, {
                      state: {
                        contract: t,
                        from: location.pathname,
                        historyStack: [
                          ...(location.state?.historyStack || []),
                          location.pathname,
                        ],
                      },
                    });
                  }}
                  className="group bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition cursor-pointer hover:border-[#375AE7]/30"
                >
                  <div className="flex items-center justify-start gap-3">
                    {/* ICON */}
                    <div className="w-10 h-10 rounded-xl bg-[#375AE7]/10 flex items-center justify-center mb-4 group-hover:bg-[#375AE7]/20 transition">
                      <FileText className="text-[#375AE7]" size={18} />
                    </div>

                    {/* TITLE */}
                    <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[#375AE7] transition whitespace-normal break-words capitalize">
                      {t.title || 'Untitled Contract'}
                    </h3>
                  </div>
                  {/* META */}
                  <div className="text-xs text-gray-500 flex items-center justify-between mt-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                        t.customer_status?.toLowerCase() === 'signed'
                          ? 'bg-green-100 text-green-700'
                          : t.customer_status?.toLowerCase() === 'viewed' ||
                              t.customer_status?.toLowerCase() === 'sent'
                            ? 'bg-blue-100 text-blue-700'
                            : t.customer_status?.toLowerCase() === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : t.customer_status?.toLowerCase() === 'declined'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {t.customer_status?.charAt(0).toUpperCase() +
                        t.customer_status?.slice(1).toLowerCase()}
                    </span>
                    <span>v{t.version_number}</span>
                  </div>

                  {/* FOOTER ACTION */}
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">Click to open</span>

                    <Sparkles
                      size={16}
                      className="text-gray-300 group-hover:text-[#375AE7] transition"
                    />
                  </div>
                </div>
              ))}
        </div>
        {/* PAGINATION */}
        {isLoading ? (
          <div className="flex justify-between items-center mt-6 animate-pulse">
            <div className="h-4 w-48 bg-gray-200 rounded" />

            <div className="flex gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-8 w-8 bg-gray-200 rounded" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {' '}
            {contracts.length > 0 && (
              <div className="flex flex-wrap justify-between items-center mt-4 space-y-4 sm:space-y-0">
                {/* Entries Info Section */}
                <div className="text-[#8C8C8C] text-sm sm:text-base w-full sm:w-auto text-center sm:text-left">
                  Showing {showRecords * (currentPage - 1) + 1} to{' '}
                  {currentPage === totalPages || totalPages == 0
                    ? totalCount
                    : Math.max(
                        showRecords * currentPage,
                        contracts.length,
                      )}{' '}
                  of {totalCount} entries
                </div>

                {/* Pagination Section */}
                <div className="flex flex-wrap items-center justify-center sm:justify-end font-semibold text-sm gap-2 w-full sm:w-auto">
                  {/* Previous Button */}
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>

                  {/* Page Buttons */}
                  {Array.from({ length: Math.min(totalPages, 5) }).map(
                    (_, index) => {
                      const startPage = Math.max(
                        1,
                        Math.min(totalPages - 4, currentPage - 2),
                      );
                      const page = startPage + index;

                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 rounded ${
                            currentPage === page
                              ? 'bg-[#375AE7] text-white'
                              : 'bg-white border border-gray-300 text-black'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    },
                  )}

                  {/* Next Button */}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
const ContractCardShimmer = () => {
  return (
    <div className="bg-white border rounded-2xl p-5 shadow-sm animate-pulse">
      {/* HEADER */}
      <div className="flex items-center gap-3">
        {/* ICON */}
        <div className="w-10 h-10 rounded-xl bg-gray-200" />

        {/* TITLE */}
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
      </div>

      {/* META */}
      <div className="flex items-center justify-between mt-5">
        <div className="h-6 w-20 bg-gray-200 rounded-full" />
        <div className="h-3 w-10 bg-gray-200 rounded" />
      </div>

      {/* FOOTER */}
      <div className="flex items-center justify-between mt-5">
        <div className="h-3 w-24 bg-gray-100 rounded" />

        <div className="w-4 h-4 rounded-full bg-gray-200" />
      </div>
    </div>
  );
};
